import type { PageContext } from '../lib/types';
// Flow Marketplace — buy ready-made flows from other studios, or sell your own.
// Purchases become Assets templates (and optionally a new draft flow in a project).
import { html, toast } from '../lib/dom';
import * as store from '../store/ui';
import { icon } from '../components/icons';
import { coverThumb } from '../components/thumb';
import { pageHead, tabs, searchInput, select, empty, kpi, table, sectionHead, avatar } from '../components/ui';
import { listingCard, workCard, priceTag, stars, creatorLine, listingStatus, chainPreview, reviewRow, bindCommunity } from '../components/community';
import { openPurchase, openAddToProject, openSellFlow } from '../components/marketplace-modals';
import {
  listListings, getListing, getCreator, isForSale, isOwnListing, listingWorks, listingReviews, creatorListings, categoryCounts,
  myListings, myPurchases, purchaseOf, sellerStats, setListingStatus, CATEGORIES, LICENSES, PLATFORM_FEE, LISTING_STATUS_LABEL,
} from '../api/marketplace';
import { getFlow } from '../api/flows';
import { getUser } from '../api/workspace';
import { money, num, relTime, shortDate } from '../lib/format';

const SORTS = [['popular', 'Most popular'], ['newest', 'Newest'], ['rating', 'Top rated'], ['price_asc', 'Price: low to high'], ['price_desc', 'Price: high to low']];

function statusAction(l?: any) {
  if (l.status === 'draft' || l.status === 'rejected') return html`<button class="btn sm" data-set-status="${l.id}:in_review">Submit for review</button>`;
  if (l.status === 'published') return html`<button class="btn sm ghost" data-set-status="${l.id}:unlisted">Unlist</button>`;
  if (l.status === 'unlisted') return html`<button class="btn sm" data-set-status="${l.id}:published">Relist</button>`;
  return html`<span class="mono muted" style="font-size:11px">Waiting for review</span>`;
}

function browse(q?: any) {
  const filtered = Boolean(q.q || q.category || q.price);
  const rows = listListings({ q: q.q, category: q.category, price: q.price, sort: q.sort });
  const counts = categoryCounts();
  const total = Object.values(counts).reduce((a?: any, b?: any) => a + b, 0);
  const best = filtered ? [] : listListings({ sort: 'popular' }).filter((l?: any) => !isOwnListing(l)).slice(0, 3);
  return html`
    <div class="toolbar">
      ${searchInput(q.q, 'Search flows, sellers, models…')}
      ${select('price', [['free', 'Free'], ['paid', 'Paid']], q.price, { placeholder: 'Any price' })}
      <span class="spacer"></span>
      ${select('sort', SORTS, q.sort || 'popular')}
    </div>
    <div class="type-chips"><button class="${!q.category ? 'on' : ''}" data-param="category" data-value="">All<span class="n">${total}</span></button>${CATEGORIES.map((c?: any) => html`<button class="${q.category === c ? 'on' : ''}" data-param="category" data-value="${c}">${c}<span class="n">${counts[c]}</span></button>`)}</div>
    ${best.length ? html`<section class="section">${sectionHead('Best sellers')}<div class="grid c3">${best.map(listingCard)}</div></section>` : ''}
    <section class="section">
      ${best.length ? sectionHead('All flows') : ''}
      ${rows.length ? html`<div class="grid auto">${rows.map(listingCard)}</div>` : empty('No flows match these filters.', html`<a class="btn sm" href="#/marketplace">Clear filters</a>`)}
    </section>`;
}

function selling() {
  const st = sellerStats();
  const rows = myListings();
  return html`
    <div class="grid c4">
      ${kpi('Earnings', money(st.payout), `after the ${PLATFORM_FEE * 100}% platform fee`)}
      ${kpi('Sales', num(st.sales))}
      ${kpi('Live listings', st.live, st.inReview ? `${st.inReview} in review` : '')}
      ${kpi('Avg rating', st.rating ? `${st.rating.toFixed(1)} ★` : '—')}
    </div>
    <div class="card">${table([
      { label: 'Listing', render: (l?: any) => html`<div class="cell-entity">${coverThumb(l)}<div class="col" style="gap:0"><strong style="font-weight:500">${l.title}</strong><span class="muted" style="font-size:12px">${l.category} · from ${getFlow(l.source_flow_id)?.name || 'flow'}</span></div></div>` },
      { label: 'Status', render: listingStatus },
      { label: 'Price', render: (l?: any) => priceTag(l.price) },
      { label: 'Sales', num: true, render: (l?: any) => num(l.sales_count) },
      { label: 'Earned', num: true, render: (l?: any) => money(l.price * l.sales_count * (1 - PLATFORM_FEE), 0) },
      { label: 'Rating', render: (l?: any) => (l.rating ? html`${stars(l.rating)} <span class="mono" style="font-size:11px">${l.rating.toFixed(1)}</span>` : html`<span class="muted">—</span>`) },
      { label: 'Updated', render: (l?: any) => relTime(l.updated_at) },
      { label: '', render: statusAction },
    ], rows, { rowHref: (l?: any) => `#/marketplace/${l.id}`, empty: 'You haven’t listed any flows yet.' })}</div>`;
}

function purchased() {
  const rows = myPurchases();
  return html`<div class="card">${table([
    { label: 'Flow', render: (p?: any) => html`<div class="cell-entity">${coverThumb(p.listing)}<div class="col" style="gap:0"><strong style="font-weight:500">${p.listing.title}</strong><span class="muted" style="font-size:12px">by ${getCreator(p.listing.seller_id).name}</span></div></div>` },
    { label: 'Paid', render: (p?: any) => priceTag(p.price) },
    { label: 'License', render: (p?: any) => p.license },
    { label: 'Bought by', render: (p?: any) => getUser(p.buyer_user_id).name },
    { label: 'Date', render: (p?: any) => shortDate(p.created_at) },
    { label: 'In Assets', render: (p?: any) => html`<a href="#/assets/${p.asset_id}" style="color:var(--fern)">Template →</a>` },
    { label: '', render: (p?: any) => (p.flow_id && getFlow(p.flow_id)
      ? html`<a class="btn sm" href="#/projects/${getFlow(p.flow_id).project_id}/flows/${p.flow_id}">Open flow</a>`
      : html`<button class="btn sm primary" data-add-project="${p.id}">${icon('plus')}Add to project</button>`) },
  ], rows, { rowHref: (p?: any) => `#/marketplace/${p.listing_id}`, empty: 'Nothing bought yet.' })}</div>`;
}

export function renderList(ctx: PageContext) {
  const q = ctx.query;
  const tab = ['selling', 'purchased'].includes(q.tab) ? q.tab : 'browse';
  const body = html`<div class="page">
    ${pageHead({
      eyebrow: 'Community', title: 'Marketplace',
      lede: 'Buy ready-made flows from other studios, or sell your own. A purchase lands in Assets as a template and can be added to any project as an editable flow.',
      actions: html`<a class="btn" href="#/gallery">${icon('gallery')}Gallery</a><button class="btn primary" data-sell>${icon('tag')}Sell a flow</button>`,
    })}
    ${tabs([['browse', 'Browse'], ['selling', 'My listings', myListings().length], ['purchased', 'Purchased', myPurchases().length]], tab)}
    ${tab === 'selling' ? selling() : tab === 'purchased' ? purchased() : browse(q)}
  </div>`;

  return {
    title: 'Marketplace', body,
    mount(root?: any) {
      root.querySelector('[data-sell]')?.addEventListener('click', () => openSellFlow());
      root.querySelectorAll('[data-set-status]').forEach((b?: any) => b.addEventListener('click', (e?: any) => {
        e.stopPropagation();
        const [id, to] = b.dataset.setStatus.split(':');
        setListingStatus(id, to);
        toast(`${getListing(id).title} → ${LISTING_STATUS_LABEL[to]}`);
        ctx.rerender();
      }));
      root.querySelectorAll('[data-add-project]').forEach((b?: any) => b.addEventListener('click', (e?: any) => {
        e.stopPropagation();
        openAddToProject(myPurchases().find((p?: any) => p.id === b.dataset.addProject));
      }));
    },
  };
}

function buyCard(l?: any) {
  const own = isOwnListing(l);
  const bought = purchaseOf(l.id);
  const flow = own ? getFlow(l.source_flow_id) : null;
  let actions;
  if (own) {
    actions = html`<div class="row wrap">${listingStatus(l)}<span class="spacer"></span>${statusAction(l)}</div>
      ${flow ? html`<a class="btn" href="#/projects/${flow.project_id}/flows/${flow.id}">${icon('node')}Open source flow</a>` : ''}`;
  } else if (bought) {
    const copy = bought.flow_id && getFlow(bought.flow_id);
    actions = html`<div class="banner ok">${icon('check')}<div>Purchased ${relTime(bought.created_at)} · ${bought.license} license</div></div>
      ${copy ? html`<a class="btn primary" href="#/projects/${copy.project_id}/flows/${copy.id}">${icon('node')}Open your copy</a>` : html`<button class="btn primary" data-add-project="${bought.id}">${icon('plus')}Add to project</button>`}
      <a class="btn ghost" href="#/assets/${bought.asset_id}">Find in Assets</a>`;
  } else {
    actions = html`<button class="btn primary lg" data-buy style="width:100%">${icon('bag')}${l.price ? `Buy for ${money(l.price, 0)}` : 'Get for free'}</button>`;
  }
  return html`<section class="card pad-lg col" style="gap:14px">
    <div class="row"><span class="buy-price">${l.price ? money(l.price, 0) : 'Free'}</span><span class="spacer"></span><span class="chip">${l.license}</span></div>
    <p class="muted" style="font-size:13px">${LICENSES[l.license]}</p>
    ${actions}
    <ul class="buy-points">
      <li>${icon('copy')}<span>An editable copy of the ${l.node_count}-node flow</span></li>
      <li>${icon('library')}<span>Saved to Assets as a template</span></li>
      <li>${icon('plug')}<span>Runs on your own connections${l.requirements.length ? `: ${l.requirements.join(', ')}` : ''}</span></li>
      <li>${icon('retry')}<span>Updates within v${l.version}</span></li>
    </ul>
  </section>`;
}

export function renderDetail(ctx: PageContext) {
  const l = getListing(ctx.params.listingId);
  if (!l || (!isForSale(l) && !isOwnListing(l) && !purchaseOf(l.id))) {
    return { title: 'Not found', body: html`<div class="page">${empty('This listing isn’t available.', html`<a class="btn sm" href="#/marketplace">Back to Marketplace</a>`)}</div>` };
  }
  const c = getCreator(l.seller_id);
  const works = listingWorks(l.id);
  const reviews = listingReviews(l.id);
  const more = creatorListings(c.id, l.id).slice(0, 3);
  const following = store.isFollowing(c.id);

  const body = html`<div class="page">
    <a class="btn ghost sm" href="#/marketplace" style="align-self:flex-start">${icon('back')}Marketplace</a>
    ${!isForSale(l) ? html`<div class="banner info">${icon('tag')}<div><strong>${LISTING_STATUS_LABEL[l.status]}.</strong> Buyers can’t see this listing until it’s live.</div></div>` : ''}
    <header class="col" style="gap:10px">
      <span class="eyebrow">${l.category}</span>
      <h1>${l.title}</h1>
      <p class="lede" style="margin:0">${l.tagline}</p>
      <div class="row wrap" style="gap:16px">
        ${creatorLine(c, { handle: false })}
        ${l.rating ? html`<span class="row" style="gap:6px">${stars(l.rating)}<span class="mono" style="font-size:12px">${l.rating.toFixed(1)} (${num(l.reviews_count)} reviews)</span></span>` : ''}
        <span class="mono muted" style="font-size:12px">${num(l.sales_count)} ${l.price ? 'sales' : 'installs'}</span>
        <span class="mono muted" style="font-size:12px">v${l.version} · updated ${relTime(l.updated_at)}</span>
      </div>
    </header>

    <div class="split">
      <div class="col" style="gap:28px">
        ${works.length
          ? html`<section class="section"><div class="section-head"><h2>Made with this flow</h2><a class="link" href="#/gallery?sale=1">Gallery →</a></div><div class="masonry compact">${works.map(workCard)}</div></section>`
          : html`<div class="card" style="overflow:hidden">${coverThumb(l, 'thumb hero')}</div>`}
        <section class="section"><h2>About</h2><p>${l.description}</p></section>
        <section class="section">
          <h2>What’s inside</h2>
          ${chainPreview(l.chain)}
          <div class="grid c3">
            ${kpi('Nodes', l.node_count)}
            ${kpi('Cost per run', `≈ ${money(l.est_cost_per_run, 3)}`, 'billed by your own providers')}
            ${kpi('Models', l.models.length || '—', l.models.join(', '))}
          </div>
        </section>
        <section class="section">
          <div class="section-head"><h2>Reviews</h2>${l.rating ? html`<span class="row" style="gap:6px;margin-left:auto">${stars(l.rating)}<span class="mono" style="font-size:12px">${l.rating.toFixed(1)} · ${num(l.reviews_count)}</span></span>` : ''}</div>
          ${reviews.length ? html`<div class="card pad">${reviews.map(reviewRow)}</div>` : empty('No reviews yet.')}
        </section>
      </div>

      <aside class="col" style="gap:16px;position:sticky;top:20px">
        ${buyCard(l)}
        <section class="card pad col" style="gap:12px">
          <div class="row" style="gap:10px">${avatar(c)}<div class="col" style="gap:0;flex:1;min-width:0"><span class="row" style="gap:6px"><strong style="font-weight:500">${c.name}</strong>${c.verified ? html`<span class="verified" title="Verified creator">${icon('check')}</span>` : ''}</span><span class="mono muted" style="font-size:11px">@${c.handle} · ${num(c.followers + (following ? 1 : 0))} followers</span></div>
            ${isOwnListing(l) ? html`<span class="chip">You</span>` : html`<button class="btn sm ${following ? '' : 'primary'}" data-follow="${c.id}">${following ? 'Following' : 'Follow'}</button>`}</div>
          <p class="muted" style="font-size:13px">${c.bio}</p>
          ${more.length ? html`<div class="eyebrow">More from this seller</div>${more.map((x?: any) => html`<a class="list-row" href="#/marketplace/${x.id}">${coverThumb(x)}<div class="col" style="gap:2px;flex:1;min-width:0"><span class="t ellipsis">${x.title}</span><span class="s">${num(x.sales_count)} sales</span></div>${priceTag(x.price)}</a>`)}` : ''}
        </section>
      </aside>
    </div>
  </div>`;

  return {
    title: l.title, body,
    mount(root?: any) {
      bindCommunity(root, ctx);
      root.querySelector('[data-buy]')?.addEventListener('click', () => openPurchase(l));
      root.querySelector('[data-add-project]')?.addEventListener('click', (e?: any) => openAddToProject(myPurchases().find((p?: any) => p.id === e.currentTarget.dataset.addProject)));
      root.querySelectorAll('[data-set-status]').forEach((b?: any) => b.addEventListener('click', () => {
        const [id, to] = b.dataset.setStatus.split(':');
        setListingStatus(id, to);
        toast(`Listing → ${LISTING_STATUS_LABEL[to]}`);
        ctx.rerender();
      }));
    },
  };
}
